// Раздел «Найм»: воронка кандидатов, единый чёрный список, площадка «Авито» и помощник
// переписки на живых серверных данных.
//
// Скелет тот же, что у всех разделов (правило 1): колонка списка с переключателем
// представлений и отборами, колонка детали с вкладками. Новых поверхностей раздел не заводит —
// правило 12, и потолок разделов уже превышен на один («Дела», отступление записано датой в
// ПРАВИЛА.md 1.1).
//
// Три представления полноширинные и колонки списка не имеют вовсе: воронка, журнал сверок и
// библиотека формулировок. Причина одна на все три — у них нет «выбранной строки», ради
// которой существует правая колонка: воронка это восемь ступеней и конверсия между ними,
// журнал сверок читается сверху вниз, а у формулировки все поля помещаются в самой строке.
// Пустая правая колонка рядом с ними была бы поверхностью, которая ничего не означает.
//
// Всё состояние экрана лежит в адресе (правило 4): представление, поиск, отбор, выбранная
// строка, вкладка карточки и открытый мастер.

import type { ReactNode } from 'react';

import { RiCompass3Line, RiLockLine } from '@remixicon/react';
import { Link, useLocation, useParams } from 'react-router';

import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { parseSearch } from '@/shared/routing/search';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState } from '@/shared/ui/screen-state';

import { ViewSwitcher, allowedViews } from './components/ViewSwitcher';
import { AccountsList } from './views/AccountsList';
import { BlacklistList } from './views/BlacklistList';
import { CandidatesList } from './views/CandidatesList';
import { ChecksView } from './views/ChecksView';
import { DialogsList } from './views/DialogsList';
import { DraftsList } from './views/DraftsList';
import { FunnelView } from './views/FunnelView';
import { LibraryView } from './views/LibraryView';
import { AccountDetail } from './card/AccountCard';
import { BlacklistDetail } from './card/BlacklistCard';
import { CandidateDetail } from './card/CandidateCard';
import { DialogDetail } from './card/DialogCard';
import { DraftDetail } from './card/DraftCard';
import type { HiringRoute } from './route';
import { DEFAULT_VIEW, VIEW_PERMISSION, VIEW_TITLES, hiringHref, isHiringView } from './route';

// Побочный эффект: раздел объявляет каркасу свои панели-мастера.
import './panels/register';

export function HiringSection({ view: viewFromShell }: { view?: string }) {
  const params = useParams<{ view?: string; rowId?: string }>();
  const location = useLocation();
  const permissions = useAuthStore((state) => state.permissions);

  const rawView = params.view ?? viewFromShell;
  const search = parseSearch(location.search);

  if (rawView !== undefined && !isHiringView(rawView)) {
    return (
      <Полноэкранно>
        <EmptyState
          icon={RiCompass3Line}
          title='Такого экрана в разделе «Найм» нет'
          description='В разделе кандидаты и воронка, чёрный список с предложениями и сверками, площадка «Авито» и переписка помощника. Похоже, ссылка устарела или в ней опечатка.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={hiringHref(DEFAULT_VIEW, { filters: {}, panels: [] })}>
                К списку кандидатов
              </Link>
            </LinkButton.Root>
          }
        />
      </Полноэкранно>
    );
  }

  const view = isHiringView(rawView) ? rawView : DEFAULT_VIEW;
  const route: HiringRoute = { view, rowId: params.rowId, search };

  // Прямая ссылка на представление, до чьего права человек не дотянулся: не «доступ запрещён»,
  // а причина и шаг (правило 10). Шаг здесь настоящий — соседнее представление того же
  // раздела, которое человеку положено: финотдел, открывший ссылку на кандидатов, уходит в
  // чёрный список, а не на стартовый экран.
  if (!can(permissions, VIEW_PERMISSION[view])) {
    const запасное = allowedViews(permissions)[0];
    return (
      <Полноэкранно>
        <EmptyState
          icon={RiLockLine}
          title={`«${VIEW_TITLES[view]}» не входит в ваши права`}
          description={
            запасное === undefined
              ? 'В разделе «Найм» вам пока не открыто ни одно представление. Права выдаёт администратор системы: попросите его открыть нужное и войдите заново.'
              : `Это представление открыто рекрутёру, кадрам и руководству. Права выдаёт администратор системы — а пока в разделе вам доступно «${VIEW_TITLES[запасное]}».`
          }
          action={
            запасное === undefined ? undefined : (
              <LinkButton.Root variant='primary' size='medium' underline asChild>
                <Link to={hiringHref(запасное, { filters: {}, panels: [] })}>
                  Открыть «{VIEW_TITLES[запасное]}»
                </Link>
              </LinkButton.Root>
            )
          }
        />
      </Полноэкранно>
    );
  }

  // Переключатель представлений стоит НАД обеими колонками и один на весь раздел — как в
  // «Расчёте». Раньше он ехал внутрь каждого представления: в списочных сидел в колонке слева
  // и там же ломался (восемь названий в 416 точках рисовались друг поверх друга), а в
  // полноширинных уезжал наверх. Навигация, меняющая место при переходе между своими же
  // вкладками, теряет человека не хуже, чем нечитаемая.
  //
  // Полноширинные представления («Воронка», «Сверки», «Библиотека») колонки списка не имеют
  // вовсе: у них нет выбранной строки, ради которой существует правая колонка.
  return (
    <div className='flex h-full min-w-0 flex-1 flex-col'>
      <ViewSwitcher route={route} />
      <div className='flex min-h-0 min-w-0 flex-1'>{содержимое(route)}</div>
    </div>
  );
}

/** Что стоит под переключателем: полноширинное представление либо пара «список и деталь». */
function содержимое(route: HiringRoute) {
  switch (route.view) {
    case 'funnel':
      return <FunnelView route={route} />;
    case 'checks':
      return <ChecksView route={route} />;
    case 'library':
      return <LibraryView route={route} />;
    default:
      return (
        <>
          {route.view === 'candidates' && <CandidatesList route={route} />}
          {route.view === 'blacklist' && <BlacklistList route={route} />}
          {route.view === 'drafts' && <DraftsList route={route} />}
          {route.view === 'accounts' && <AccountsList route={route} />}
          {route.view === 'dialogs' && <DialogsList route={route} />}
          <SectionDetail route={route} />
        </>
      );
  }
}

/** Колонка детали: что открыто в текущем представлении. */
function SectionDetail({ route }: { route: HiringRoute }) {
  switch (route.view) {
    case 'candidates':
      return <CandidateDetail route={route} />;
    case 'blacklist':
      return <BlacklistDetail route={route} />;
    case 'drafts':
      return <DraftDetail route={route} />;
    case 'accounts':
      return <AccountDetail route={route} />;
    case 'dialogs':
      return <DialogDetail route={route} />;
    default:
      return null;
  }
}

function Полноэкранно({ children }: { children: ReactNode }) {
  return (
    <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>{children}</main>
  );
}
